import { useState, type FormEvent } from "react";
import { CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { PublicShell } from "@/components/layout/PublicShell";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import { api, ApiError } from "@/lib/api";
import { containsPatientIdentifier, type MemberRole } from "@/lib/board";
import { SEO } from "@/lib/seo";
import { Button, ButtonLink, EmptyState, Notice, Panel, TextAreaField, TextField } from "@/components/system";

const AHPRA_NUMBER = /^[A-Z]{3}\d{10}$/;

export default function Join() {
  const { city } = useCity();
  const [role, setRole] = useState<MemberRole>("gp");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [ahpraNumber, setAhpraNumber] = useState("");
  const [practiceName, setPracticeName] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const ahpra = ahpraNumber.trim().toUpperCase();
    if (!AHPRA_NUMBER.test(ahpra)) {
      setError("Enter your 3-letter, 10-digit AHPRA registration number (for example MED1234567890).");
      return;
    }
    if (containsPatientIdentifier(message)) {
      setError("Remove patient identifiers, Medicare numbers and patient-linked dates from your message.");
      return;
    }
    setStatus("submitting");
    setError(null);
    try {
      await api.post<{ ok: true }>(
        `/v1/public/${city.slug}/membership`,
        {
          role,
          name: name.trim(),
          email: email.trim(),
          ahpraNumber: ahpra,
          practiceName: practiceName.trim(),
          message: message.trim(),
        },
        { skipAuth: true },
      );
      track("membership_requested", { role });
      setStatus("success");
    } catch (requestError) {
      setStatus("error");
      setError(
        requestError instanceof ApiError && requestError.code === "membership.identifier_detected"
          ? "Remove patient identifiers, Medicare numbers and patient-linked dates from your message."
          : "Your request could not be sent. Please try again.",
      );
    }
  };

  return (
    <PublicShell>
      <SEO title="Join PulseBoard" description="Request clinician membership of the PulseBoard cockpit." path="/join" noindex />
      <div className="ds-container">
        {status === "success" ? (
          <div className="ds-container--narrow" style={{ marginInline: "auto" }}>
            <div className="ds-section">
              <EmptyState
                icon={<CheckCircle2 />}
                title="Request received. We'll review it within one business day."
                action={<ButtonLink to="/" variant="secondary">Back to PulseBoard</ButtonLink>}
              >
                We have sent an acknowledgement to {email}. If approved, you'll get an email to verify your address and
                set your PulseBoard password.
              </EmptyState>
            </div>
          </div>
        ) : (
          <>
            <header className="ds-page-head">
              <p className="ds-eyebrow ds-eyebrow--teal">Private clinician membership</p>
              <h1 className="ds-display-1">Join PulseBoard</h1>
              <p className="ds-lead">
                For GPs and specialists who want a faster view of local referral availability, five-minute learning
                notes and a CPD log.
              </p>
            </header>
            <div className="ds-split">
              <Panel aria-labelledby="join-form-heading">
                <h2 id="join-form-heading" className="ds-title">Membership request</h2>
                <form className="ds-stack" style={{ gap: 16 }} onSubmit={(event) => void submit(event)}>
                  <fieldset className="ds-fieldset">
                    <legend className="ds-field__label">I am a</legend>
                    <div className="ds-seg" role="radiogroup" aria-label="Role" style={{ justifySelf: "start" }}>
                      <button type="button" role="radio" aria-checked={role === "gp"} aria-pressed={role === "gp"} onClick={() => setRole("gp")}>GP</button>
                      <button type="button" role="radio" aria-checked={role === "specialist"} aria-pressed={role === "specialist"} onClick={() => setRole("specialist")}>Specialist</button>
                    </div>
                  </fieldset>
                  <div className="ds-form-grid">
                    <TextField label="Full name" required autoComplete="name" maxLength={120} value={name} onChange={(event) => setName(event.target.value)} />
                    <TextField label="Email" required type="email" autoComplete="email" maxLength={254} value={email} onChange={(event) => setEmail(event.target.value)} />
                    <TextField
                      label="AHPRA registration number"
                      hint="3 letters followed by 10 digits."
                      required
                      inputMode="text"
                      autoCapitalize="characters"
                      spellCheck={false}
                      maxLength={13}
                      pattern="[A-Za-z]{3}[0-9]{10}"
                      value={ahpraNumber}
                      onChange={(event) => {
                        setAhpraNumber(event.target.value.replace(/\s/g, "").toUpperCase());
                        setError(null);
                      }}
                      placeholder="MED1234567890"
                    />
                    <TextField label="Practice name" maxLength={160} value={practiceName} onChange={(event) => setPracticeName(event.target.value)} placeholder="Optional" />
                    <TextAreaField
                      full
                      label={`Message (optional · ${message.length}/500)`}
                      rows={4}
                      maxLength={500}
                      value={message}
                      onChange={(event) => {
                        setMessage(event.target.value);
                        setError(null);
                      }}
                      placeholder="Anything useful for reviewing your membership. Do not include patient information."
                    />
                  </div>
                  {error ? <Notice tone="error">{error}</Notice> : null}
                  <div className="ds-cluster" style={{ justifyContent: "space-between" }}>
                    <p className="ds-fine">
                      Your details are used only to review and run your membership. Read the{" "}
                      <Link className="ds-link" to="/privacy">Privacy Policy</Link>.
                    </p>
                    <Button type="submit" disabled={status === "submitting"}>
                      {status === "submitting" ? "Sending request…" : "Request membership"}
                    </Button>
                  </div>
                </form>
                <p className="ds-small">Already a member? <Link className="ds-link" to="/auth">Sign in</Link></p>
              </Panel>
              <Panel tone="tint" aria-labelledby="join-next-heading">
                <h2 id="join-next-heading" className="ds-title">What happens next</h2>
                <ol className="ds-steps">
                  <li><strong>We review your request.</strong> Your AHPRA number is recorded and your identity is checked by PulseBoard.</li>
                  <li><strong>You get an approval email.</strong> Approved members verify their email and set a password securely.</li>
                  <li><strong>Your Board opens.</strong> Sign in to your private {city.name} clinician Board.</li>
                </ol>
              </Panel>
            </div>
          </>
        )}
      </div>
    </PublicShell>
  );
}
